import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const HomePage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showGif, setShowGif] = useState<boolean>(true);
  
  const [formData, setFormData] = useState<FormData>({
    email: '',
    type: 'user',
    ageConfirm: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Rewardly Protocol — Earn-Time Loyalty Rewards Infrastructure';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'The first protocol to guarantee loyalty reward value at earn time. $300B issued annually, ~50% wasted. 10 industries. Zero POS. Patent pending.');
    setMeta('og:title', 'Rewardly Protocol — Rewards Guaranteed at Earn Time');
    setMeta('og:description', 'The first loyalty protocol to lock value at earn time — not redemption. 10 industries. 150+ templates. Patent pending.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'Rewardly Protocol — Rewards Guaranteed at Earn Time');
    setMeta('twitter:description', 'The first loyalty protocol to lock value at earn time — not redemption. 10 industries. 150+ templates. Patent pending.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/');

    let ldScript = document.querySelector('script[type="application/ld+json"]');
    if (!ldScript) {
      ldScript = document.createElement('script');
      ldScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Rewardly Protocol",
      "applicationCategory": "BlockchainApplication",
      "description": "The first loyalty protocol to guarantee reward value at earn time — not redemption time. Patent pending.",
      "url": "https://rewardly.live",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Join the waitlist for early access"
      },
      "creator": {
        "@type": "Organization",
        "name": "Rewardly Protocol, Inc.",
        "url": "https://rewardly.live",
        "sameAs": ["https://x.com/Rewardly_io", "https://www.linkedin.com/company/rewardly-protocol-inc/"]
      }
    });

    if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
      const pc1 = document.createElement('link');
      pc1.rel = 'preconnect';
      pc1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(pc1);
      const pc2 = document.createElement('link');
      pc2.rel = 'preconnect';
      pc2.href = 'https://fonts.gstatic.com';
      pc2.crossOrigin = 'anonymous';
      document.head.appendChild(pc2);
    }
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  useEffect(() => {
    // Force scroll to top immediately and prevent browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Ensure GIF is visible
    setShowGif(true);
    
    // Auto-hide GIF after 3 seconds
    const timer = setTimeout(() => {
      setShowGif(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Stat counter animation
  useEffect(() => {
    const countUp = (element: HTMLElement, target: string) => {
      const isMonetary = target.startsWith('$');
      const suffix = target.replace(/[\d$.]/g, '');
      const numStr = target.replace(/[^0-9.]/g, '');
      const targetNum = parseFloat(numStr);
      if (targetNum === 0) { element.textContent = target; return; }
      const duration = window.innerWidth <= 768 ? 700 : 1200; // faster on mobile
      const startTime = performance.now();
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(eased * targetNum);
        element.textContent = (isMonetary ? '$' : '') + current + suffix;
        if (progress < 1) { requestAnimationFrame(animate); }
        else { element.textContent = target; }
      };
      requestAnimationFrame(animate);
    };
    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const target = el.getAttribute('data-target');
            if (target) { countUp(el, target); statsObserver.unobserve(el); }
          }
        });
      },
      { threshold: 0.5 }
    );
    const statElements = document.querySelectorAll('.stat-value[data-target]');
    statElements.forEach(el => {
      const htmlEl = el as HTMLElement;
      htmlEl.setAttribute('data-target', htmlEl.textContent || '');
      htmlEl.textContent = isNaN(parseInt(htmlEl.textContent || '')) ? htmlEl.textContent : '0';
      statsObserver.observe(el);
    });
    return () => statsObserver.disconnect();
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!validateEmail(formData.email)) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    if (!formData.ageConfirm) {
      setSubmitMessage('Please confirm you are 18 or older');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          user_type: formData.type
        })
      });

      const raw = await response.json();

      // Handle both direct response and Lambda proxy wrapped response
      let data: any;
      if (raw.body && typeof raw.body === 'string') {
        try { data = JSON.parse(raw.body); } catch { data = raw; }
      } else {
        data = raw;
      }

      const statusCode = raw.statusCode || response.status;

      if (statusCode === 200 && !data.error) {
        if (data.new_subscription) {
          setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
        } else {
          setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
        }
        setFormData({ email: '', type: 'user', ageConfirm: false });

        setTimeout(() => {
          setModalOpen(false);
          setSubmitMessage('');
        }, 3000);
      } else {
        setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting email:', error);
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (submitMessage) setSubmitMessage('');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{paddingTop: 0}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');

        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { 
          scroll-behavior: smooth;
          overflow-x: hidden;
        }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
          position: relative;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* GIF Banner Section */
        .gif-banner {
          width: 100%;
          padding-top: 72px;
          transition: opacity 0.5s ease-out, max-height 0.5s ease-out, padding 0.5s ease-out;
          overflow: visible;
          opacity: 1;
          visibility: visible;
          background: #0F172A;
        }

        .gif-banner.fade-out {
          opacity: 0;
          max-height: 0;
          padding: 0;
          visibility: hidden;
          overflow: hidden;
        }

        .gif-banner img {
          width: 100%;
          height: auto;
          display: block;
          animation: fadeInGif 1s ease-in;
          object-fit: cover;
          min-height: 300px;
        }

        @keyframes fadeInGif {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* Navigation */
        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover { color: var(--blue-600); }

        /* Dropdown Menu */
        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: var(--blue-600);
          color: var(--white);
        }
        .btn-primary:hover {
          background: var(--blue-700);
          transform: translateY(-1px);
        }

        .btn-outline {
          background: transparent;
          color: var(--gray-900);
          border: 1px solid var(--gray-200);
        }
        .btn-outline:hover {
          border-color: var(--blue-600);
          color: var(--blue-600);
        }

        .btn-gold {
          background: var(--gold);
          color: var(--gray-900);
        }
        .btn-gold:hover {
          background: var(--gold-dark);
          transform: translateY(-1px);
        }

        /* Mobile Menu */
        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span {
          width: 22px;
          height: 2px;
          background: var(--gray-900);
          transition: 0.2s;
        }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          max-height: calc(100vh - 72px);
          overflow-y: auto;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        /* Modal */
        .modal-overlay {
          display: none;
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }
        .modal-overlay.active { display: flex; }

        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .modal h3 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 24px;
        }

        .modal-field {
          margin-bottom: 20px;
        }

        .modal-field label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--gray-500);
          margin-bottom: 8px;
        }

        .modal-field input,
        .modal-field select {
          width: 100%;
          padding: 14px 16px;
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .modal-field input:focus,
        .modal-field select:focus {
          outline: none;
          border-color: var(--blue-600);
        }

        .modal-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 24px;
        }

        .modal-checkbox input {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: var(--blue-600);
        }

        .modal-checkbox span {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.5;
        }

        .modal-buttons {
          display: flex;
          gap: 12px;
        }

        .modal-buttons .btn {
          flex: 1;
          justify-content: center;
          padding: 14px 20px;
        }

        .submit-message {
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .submit-message.success {
          background: #DEF7EC;
          color: #03543F;
        }

        .submit-message.error {
          background: #FDE8E8;
          color: #9B1C1C;
        }

        /* Hero */
        .hero {
          padding: 140px 0 80px;
          background: linear-gradient(135deg, var(--off-white) 0%, var(--white) 100%);
        }

        .hero-content {
          text-align: center;
          max-width: 900px;
          margin: 0 auto;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
          background: linear-gradient(135deg, var(--gray-900) 0%, var(--blue-600) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-family: 'Crimson Text', serif;
          font-size: 22px;
          font-style: italic;
          color: var(--gold);
          margin-bottom: 16px;
          letter-spacing: 0.3px;
        }

        .hero p {
          font-size: 20px;
          color: var(--gray-500);
          line-height: 1.6;
          margin-bottom: 32px;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-bottom: 60px;
          flex-wrap: wrap;
        }

        .patent-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          border-radius: 20px;
          background: rgba(212,168,83,0.1);
          border: 1px solid rgba(212,168,83,0.25);
          font-size: 11px;
          font-weight: 600;
          color: var(--gold-dark);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 24px;
        }
        .patent-badge .pulse-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--gold);
          position: relative;
        }
        .patent-badge .pulse-dot::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          border: 1.5px solid var(--gold);
          transform: translate(-50%, -50%);
          animation: radarPing 2.5s ease-out infinite;
        }
        @keyframes radarPing {
          0% { width: 6px; height: 6px; opacity: 0.8; }
          100% { width: 24px; height: 24px; opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .patent-badge .pulse-dot::after { animation: none; }
        }

        /* Stats */
        .stats-row {
          display: flex;
          justify-content: center;
          gap: 64px;
          max-width: 700px;
          margin: 0 auto;
        }

        .stat {
          text-align: center;
        }

        .stat-value {
          font-size: 36px;
          font-weight: 700;
          color: var(--blue-600);
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: var(--gray-500);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        /* Sections */
        section {
          padding: 56px 0;
        }

        .section-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .section-label {
          display: inline-block;
          background: var(--blue-600);
          color: var(--white);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 700;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 18px;
          color: var(--gray-500);
          max-width: 600px;
          margin: 0 auto;
        }

        /* Problem Cards */
        .problems-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .problem-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
          transition: all 0.3s;
        }

        .problem-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .problem-icon {
          width: 48px;
          height: 48px;
          background: var(--gray-100);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          color: var(--blue-600);
        }

        .problem-icon svg {
          stroke: currentColor;
        }

        .problem-card h3 {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .problem-card p {
          color: var(--gray-500);
          line-height: 1.6;
        }

        /* How It Works */
        .flow-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 900px;
          margin: 0 auto;
          padding: 40px;
          background: var(--white);
          border-radius: 16px;
          border: 1px solid var(--gray-200);
        }

        .flow-step {
          flex: 1;
          text-align: center;
        }

        .flow-step-icon {
          width: 80px;
          height: 80px;
          background: var(--gray-100);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: var(--blue-600);
        }

        .flow-step-icon svg {
          width: 40px;
          height: 40px;
          stroke: currentColor;
        }

        .flow-step h4 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .flow-step span {
          font-size: 14px;
          color: var(--gray-500);
        }

        .flow-arrow {
          color: var(--gray-400);
          font-size: 24px;
          margin: 0 20px;
        }

        /* Ecosystem */
        .ecosystem-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .ecosystem-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
        }

        .ecosystem-card h3 {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 24px;
        }

        .ecosystem-list {
          list-style: none;
        }

        .ecosystem-list li {
          display: flex;
          align-items: start;
          gap: 12px;
          margin-bottom: 16px;
          color: var(--gray-500);
        }

        .check-icon {
          width: 20px;
          height: 20px;
          background: var(--green-600);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--white);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .check-icon svg {
          width: 12px;
          height: 12px;
          stroke: currentColor;
        }

        /* Innovations */
        .innovations-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .innovation-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
          transition: all 0.3s;
        }

        .innovation-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .innovation-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: var(--white);
        }

        .innovation-icon svg {
          width: 28px;
          height: 28px;
          stroke: currentColor;
        }

        .innovation-card h3 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .innovation-card p {
          color: var(--gray-500);
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .innovation-stats {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .innovation-stat {
          background: var(--gray-100);
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--blue-600);
        }

        /* Competitive Stats */
        .competitive-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .competitive-stat {
          text-align: center;
          padding: 32px 16px;
          background: var(--white);
          border-radius: 12px;
          border: 1px solid var(--gray-200);
        }

        .competitive-value {
          font-size: 36px;
          font-weight: 800;
          color: var(--blue-600);
          margin-bottom: 8px;
        }

        .competitive-label {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.4;
        }

        /* Platform Layers */
        .layers-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .layer-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
          position: relative;
          overflow: hidden;
        }

        .layer-card.priority {
          border-color: var(--gold);
          box-shadow: 0 0 0 1px var(--gold);
        }

        .layer-badge {
          display: inline-block;
          background: var(--gold);
          color: var(--gray-900);
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .layer-card h3 {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .layer-card .layer-desc {
          font-size: 14px;
          color: var(--gray-500);
          margin-bottom: 20px;
          line-height: 1.6;
        }

        .layer-features {
          list-style: none;
          padding: 0;
        }

        .layer-features li {
          display: flex;
          align-items: start;
          gap: 10px;
          margin-bottom: 12px;
          color: var(--gray-500);
          font-size: 14px;
        }

        .layer-features .check-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        /* CTA Section */
        .cta-section {
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          padding: 80px 0;
          text-align: center;
          color: var(--white);
        }

        .cta-section h2 {
          font-size: 40px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .cta-section p {
          font-size: 18px;
          margin-bottom: 32px;
          opacity: 0.9;
        }

        /* Footer */
        footer {
          background: var(--gray-900);
          color: var(--white);
          padding: 60px 0 40px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-brand h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .footer-brand p {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .footer-links h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .footer-grid a {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }

        .footer-grid a:hover {
          color: var(--white);
        }

        .footer-social h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.6);
          transition: all 0.2s;
        }

        .social-links a:hover {
          background: rgba(255,255,255,0.2);
          color: var(--white);
        }

        .footer-bottom {
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        /* Inversion Section */
        .inversion-section {
          padding: 80px 0;
          background: var(--gray-900);
          color: #fff;
        }
        .inversion-label {
          display: inline-block;
          background: var(--gold);
          color: var(--gray-900);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }
        .inversion-title {
          font-size: clamp(24px, 3.5vw, 36px);
          font-weight: 700;
          margin-bottom: 48px;
          line-height: 1.3;
          max-width: 700px;
        }
        .inversion-title em {
          font-family: 'Crimson Text', serif;
          font-style: italic;
          color: var(--gold);
          font-weight: 400;
        }
        .compare-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        .compare-card {
          border-radius: 12px;
          padding: 32px;
        }
        .compare-old {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .compare-new {
          background: rgba(212,168,83,0.08);
          border: 1px solid rgba(212,168,83,0.2);
        }
        .compare-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .compare-old h3 { color: var(--gray-400); }
        .compare-new h3 { color: var(--gold); }
        .compare-tag {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 24px;
        }
        .compare-old .compare-tag { background: rgba(239,68,68,0.12); color: #F87171; }
        .compare-new .compare-tag { background: rgba(212,168,83,0.15); color: var(--gold); }
        .compare-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          font-size: 14px;
          line-height: 1.5;
          color: rgba(255,255,255,0.8);
        }
        .compare-item:last-child { border-bottom: none; }
        .compare-icon { flex-shrink: 0; }
        .compare-old .compare-icon { color: #F87171; }
        .compare-new .compare-icon { color: var(--gold); }

        /* Scroll reveal animations */
        .reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .reveal-delay {
          transition-delay: 0.2s;
        }
        @media (prefers-reduced-motion: reduce) {
          .reveal { opacity: 1; transform: none; transition: none; }
        }

        /* Section divider */
        .section-divider {
          width: 48px;
          height: 2px;
          background: var(--gold);
          margin: 0 auto;
          opacity: 0.4;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }

          .gif-banner {
            padding-top: 72px;
          }

          section { padding: 40px 0; }

          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }
          .hero-subtitle { font-size: 18px; }
          .hero p { font-size: 16px; }
          .hero-buttons {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .hero-buttons .btn { justify-content: center; }

          .stats-row { flex-wrap: wrap; gap: 24px; justify-content: center; }
          .stat-value { font-size: 28px; }

          .inversion-section { padding: 48px 0; }
          .inversion-title { margin-bottom: 24px; }
          .compare-grid { grid-template-columns: 1fr; }
          .compare-card { padding: 20px; }

          .problems-grid { grid-template-columns: 1fr; }
          .innovations-grid { grid-template-columns: 1fr; }
          .competitive-row { grid-template-columns: repeat(2, 1fr); }
          .layers-grid { grid-template-columns: 1fr; }
          .ecosystem-grid { grid-template-columns: 1fr; }

          .flow-container {
            flex-direction: column;
            padding: 24px;
          }
          .flow-arrow {
            transform: rotate(90deg);
            margin: 20px 0;
          }

          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }

          .modal-buttons {
            flex-direction: column;
          }

          .reveal {
            transition-delay: 0s !important;
            transition-duration: 0.35s;
          }

          .patent-badge .pulse-dot::after {
            animation: radarPingMobile 2.5s ease-out infinite;
          }
          @keyframes radarPingMobile {
            0% { width: 6px; height: 6px; opacity: 0.8; }
            100% { width: 16px; height: 16px; opacity: 0; }
          }
        }
      `}</style>

      {/* Modal */}
      <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* GIF Banner - Full Width */}
      <div className={`gif-banner ${!showGif ? 'fade-out' : ''}`}>
        <img 
          src="/rewardly.d68a4ba661ee5060b933.gif" 
          alt="Rewardly Animation" 
        />
      </div>

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="patent-badge">
              <span className="pulse-dot"></span>
              Patent Pending — US & International
            </div>
            <h1>Your Rewards, Guaranteed the Moment You Earn Them</h1>
            <p className="hero-subtitle">The first protocol that locks value at earn time — not redemption time.</p>
            <p>$300 billion in loyalty value is issued every year. Nearly half is never redeemed. Rewardly Protocol guarantees value the instant you earn — backed by blockchain, priced by oracle, permanent by design. No expiration. No devaluation. 150+ promotion templates across 10 industries.</p>
            <div className="hero-buttons">
              <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '14px 28px', fontSize: '15px'}}>Join the Waitlist →</button>
              <a className="btn btn-primary" href="/whitepaper" style={{padding: '14px 28px', fontSize: '15px'}}>Read the Whitepaper →</a>
              <a href="https://x.com/Rewardly_io" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{padding: '14px 28px', fontSize: '15px'}}>Follow Updates</a>
            </div>
          </div>

          <div className="stats-row">
            <div className="stat">
              <div className="stat-value" data-target="$300B">$300B</div>
              <div className="stat-label">Issued Annually</div>
            </div>
            <div className="stat">
              <div className="stat-value">~50%</div>
              <div className="stat-label">Never Redeemed</div>
            </div>
            <div className="stat">
              <div className="stat-value" data-target="10">10</div>
              <div className="stat-label">Industry Verticals</div>
            </div>
            <div className="stat">
              <div className="stat-value" data-target="0">0</div>
              <div className="stat-label">Competitors</div>
            </div>
          </div>
        </div>
      </section>

      {/* Earn-Time vs Redemption-Time — The Industry Inverted */}
      <section className="inversion-section">
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div className="inversion-label">The Industry Inverted</div>
            <h2 className="inversion-title" style={{margin: '0 auto'}}>
              Every loyalty program allocates value at <em>redemption.</em><br />
              Rewardly allocates value at <em>earning.</em>
            </h2>
          </div>

          <div className="compare-grid">
            <div className="compare-card compare-old reveal">
              <h3>Every Other Program</h3>
              <div className="compare-tag">Redemption-time valuation</div>
              <div className="compare-item"><span className="compare-icon">✕</span> Points are vague promises with no guaranteed value</div>
              <div className="compare-item"><span className="compare-icon">✕</span> Nearly half of all loyalty value — $150B+ per year — goes unredeemed</div>
              <div className="compare-item"><span className="compare-icon">✕</span> Programs can devalue your points at any time</div>
              <div className="compare-item"><span className="compare-icon">✕</span> Each merchant requires POS integration</div>
              <div className="compare-item"><span className="compare-icon">✕</span> Bilateral agreements needed between every enterprise</div>
            </div>
            <div className="compare-card compare-new reveal reveal-delay">
              <h3>Rewardly Protocol</h3>
              <div className="compare-tag">Earn-time commitment · Patent Pending</div>
              <div className="compare-item"><span className="compare-icon">◆</span> RWLY committed at today's oracle price, immutably</div>
              <div className="compare-item"><span className="compare-icon">◆</span> No expiration. No devaluation. Enforced by smart contract</div>
              <div className="compare-item"><span className="compare-icon">◆</span> AI validates receipts — zero POS integration needed</div>
              <div className="compare-item"><span className="compare-icon">◆</span> One neutral token settles across all enterprise partners</div>
              <div className="compare-item"><span className="compare-icon">◆</span> Five redemption paths: merchant, gift card, bank, card, wallet</div>
            </div>
          </div>
        </div>
      </section>

      {/* Built For — Persona Routing */}
      <section style={{padding: '72px 0', background: '#fff'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div style={{display: 'inline-block', background: 'var(--blue-600)', color: '#fff', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Built For</div>
            <h2 style={{fontSize: 'clamp(24px,3vw,36px)', fontWeight: 700, color: 'var(--gray-900)'}}>Who Is This For?</h2>
          </div>
          <div className="innovations-grid" style={{gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))'}}>
            <div className="innovation-card reveal" style={{borderTop:'3px solid var(--gold)'}}>
              <div style={{width: '48px', height: '48px', background: 'var(--gold)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px'}}>
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#0F172A" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
              </div>
              <h3 className="innovation-title">You</h3>
              <p className="innovation-text">Your loyalty, your choice. Earn anywhere in the network. Redeem your way — gift cards, bank transfer, Rewardly Card, and more. No points expire. No value locked.</p>
              <button onClick={() => setModalOpen(true)} style={{display: 'inline-block', marginTop: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', padding: 0}}>Join the Waitlist →</button>
            </div>
            <div className="innovation-card reveal" style={{transitionDelay: '0.1s'}}>
              <div style={{width: '48px', height: '48px', background: 'var(--blue-600)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px'}}>
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/></svg>
              </div>
              <h3 className="innovation-title">Enterprises &amp; Coalitions</h3>
              <p className="innovation-text">Launch a branded loyalty program for your entire ecosystem. 150+ promotion templates across 10 industries. No POS integration. Live in weeks.</p>
              <a href="/coalition" style={{display: 'inline-block', marginTop: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--blue-600)', textDecoration: 'none'}}>See the Coalition Model →</a>
            </div>
            <div className="innovation-card reveal" style={{transitionDelay: '0.2s'}}>
              <div style={{width: '48px', height: '48px', background: 'var(--blue-600)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px'}}>
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>
              </div>
              <h3 className="innovation-title">Investors &amp; Partners</h3>
              <p className="innovation-text">38+ audited smart contracts on Polygon. 120B RWLY with demand-driven tokenomics.</p>
              <a href="/whitepaper" style={{display: 'inline-block', marginTop: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--blue-600)', textDecoration: 'none'}}>Read the Whitepaper →</a>
            </div>
            <div className="innovation-card reveal" style={{transitionDelay: '0.3s'}}>
              <div style={{width: '48px', height: '48px', background: 'var(--blue-600)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px'}}>
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/></svg>
              </div>
              <h3 className="innovation-title">Builders &amp; Community</h3>
              <p className="innovation-text">Open trust framework. Progressive decentralization. Join the ecosystem.</p>
              <a href="/platform" style={{display: 'inline-block', marginTop: '12px', fontSize: '14px', fontWeight: 600, color: 'var(--blue-600)', textDecoration: 'none'}}>Explore the Platform →</a>
            </div>
          </div>
        </div>
      </section>

      <div style={{padding: '8px 0', background: '#fff'}}><div className="section-divider"></div></div>

      {/* Three Innovations */}
      <section>
        <div className="container">
          <div className="section-header">
            <div className="section-label">What Sets Us Apart</div>
            <h2 className="section-title">Three Core Innovations</h2>
            <p className="section-subtitle">The infrastructure that makes universal loyalty possible.</p>
          </div>

          <div className="innovations-grid">
            <div className="innovation-card reveal">
              <div className="innovation-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/>
                </svg>
              </div>
              <h3>Smart Promotion Engine</h3>
              <p>Pre-built campaigns across 10 industries. From airline miles multipliers to government transit rewards. AI-powered creation in under 2 minutes.</p>
            </div>
            <div className="innovation-card reveal" style={{transitionDelay: '0.1s'}}>
              <div className="innovation-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"/>
                </svg>
              </div>
              <h3>AI-Powered Validation</h3>
              <p>Receipt-based earning. No POS integration. Any merchant, any industry, Day 1. Real-time rule enforcement in under 100ms.</p>
            </div>
            <div className="innovation-card reveal" style={{transitionDelay: '0.2s'}}>
              <div className="innovation-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"/>
                </svg>
              </div>
              <h3>RWLY/USDC Settlement Layer</h3>
              <p>Points convert to RWLY tokens or settle directly in USDC on Polygon. Five redemption paths. Permanent ownership. Cross-border value.</p>
            </div>
          </div>
        </div>
      </section>

      <div style={{padding: '8px 0', background: '#fff'}}><div className="section-divider"></div></div>

      {/* The White Space in Loyalty */}
      <section style={{padding: '72px 0', background: 'var(--off-white)'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div style={{display: 'inline-block', background: 'var(--blue-600)', color: '#fff', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Market Position</div>
            <h2 style={{fontSize: 'clamp(24px,3vw,36px)', fontWeight: 700, color: 'var(--gray-900)'}}>The White Space in Loyalty</h2>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '32px'}}>
            <div className="reveal" style={{background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '28px', opacity: 0.7}}>
              <div style={{fontSize: '28px', marginBottom: '12px'}}>📱</div>
              <h3 style={{fontSize: '18px', fontWeight: 700, color: 'var(--gray-500)', marginBottom: '8px'}}>Point Aggregation</h3>
              <p style={{fontSize: '14px', color: 'var(--gray-400)', lineHeight: 1.6}}>50+ apps track your points. Crowded. Commoditized. No differentiation.</p>
            </div>
            <div className="reveal" style={{background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '28px', opacity: 0.75, transitionDelay: '0.1s'}}>
              <div style={{fontSize: '28px', marginBottom: '12px'}}>🏢</div>
              <h3 style={{fontSize: '18px', fontWeight: 700, color: 'var(--gray-500)', marginBottom: '8px'}}>Enterprise Integration</h3>
              <p style={{fontSize: '14px', color: 'var(--gray-400)', lineHeight: 1.6}}>3-5 legacy players. Years to build. Requires full system replacement.</p>
            </div>
            <div className="reveal" style={{background: '#fff', border: '2px solid var(--gold, #D4A853)', borderRadius: '12px', padding: '28px', boxShadow: '0 4px 24px rgba(212,168,83,0.15)', transitionDelay: '0.2s'}}>
              <div style={{fontSize: '28px', marginBottom: '12px'}}>⭐</div>
              <h3 style={{fontSize: '18px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '8px'}}>Coalition Infrastructure</h3>
              <p style={{fontSize: '14px', color: 'var(--gray-600)', lineHeight: 1.6}}>0 competitors. This is where Rewardly Protocol operates.</p>
            </div>
          </div>
          <p style={{textAlign: 'center', color: 'var(--gray-500)', fontSize: '15px', maxWidth: '640px', margin: '0 auto'}}>
            Rewardly Protocol is the coalition layer: infrastructure for malls, campuses, and airports to launch branded loyalty on blockchain.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section style={{background: 'var(--off-white)'}}>
        <div className="container">
          <div className="section-header">
            <div className="section-label">How It Works</div>
            <h2 className="section-title">Four Simple Steps</h2>
            <p className="section-subtitle">Join the ecosystem, earn rewards, convert to RWLY, and spend anywhere.</p>
          </div>

          <div className="flow-container">
            <div className="flow-step">
              <div className="flow-step-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197"/>
                </svg>
              </div>
              <h4>Join the Ecosystem</h4>
              <span>As a user, merchant, or coalition</span>
            </div>
            <span className="flow-arrow">→</span>
            <div className="flow-step">
              <div className="flow-step-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/>
                </svg>
              </div>
              <h4>Earn Rewards</h4>
              <span>Shop, stake, mine, or participate</span>
            </div>
            <span className="flow-arrow">→</span>
            <div className="flow-step">
              <div className="flow-step-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                </svg>
              </div>
              <h4>Convert to RWLY</h4>
              <span>Instant token exchange</span>
            </div>
            <span className="flow-arrow">→</span>
            <div className="flow-step">
              <div className="flow-step-icon">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"/>
                </svg>
              </div>
              <h4>Spend Anywhere</h4>
              <span>Use across the global network</span>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Tech Strip */}
      <section style={{padding: '24px 0', background: 'var(--gray-100, #F1F5F9)'}}>
        <div className="container">
          <p style={{textAlign: 'center', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--gray-500)', margin: 0}}>
            Built on Polygon · Bridged via LayerZero &amp; Wormhole · Secured by OpenZeppelin · Priced by Chainlink
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Get Early Access</h2>
          <p>Be among the first to unlock your loyalty points.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Join Waitlist →</button>
            <a href="/whitepaper" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Read the Whitepaper →</a>
          </div>
        </div>
      </section>

      {/* Internal Links for SEO */}
      <section style={{padding: '32px 0', background: 'var(--off-white)'}}>
        <div className="container">
          <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px'}}>
            <a href="/vision" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Our Vision</a>
            <a href="/manifesto" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Manifesto</a>
            <a href="/innovation" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Innovation & IP</a>
            <a href="/platform" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Platform</a>
            <a href="/coalition" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Coalition</a>
            <a href="/whitepaper" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Whitepaper</a>
            <a href="/trust-framework" style={{fontSize: '13px', color: 'var(--gray-500)', textDecoration: 'none', padding: '6px 12px'}}>Trust Framework</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;